import { useState, useEffect } from 'react';
import { 
  Search, 
  Radio, 
  Flame,
  Plus,
  Minus,
  Trash2,
  PenLine,
  Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getAllCategories } from '../utils/categoryApi';
import { getPublicProducts } from '../utils/productApi';
import { API_CONFIG } from '../config/api';
import { formatCurrency } from '../utils/formatCurrency';
import DecimalInput from '../components/DecimalInput';
import type { Category } from '../types/category';
import type { Product } from '../types/product';
import type { CartItem } from '../types/pos';

const POS = () => {
  const navigate = useNavigate();
  
  // State management
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Order configuration
  const [orderType, setOrderType] = useState<'pickup' | 'delivery'>('pickup');
  const [customerType, setCustomerType] = useState<'walking' | 'customer'>('walking');
  const [discountValue, setDiscountValue] = useState(0);
  const [discountType, setDiscountType] = useState<'amount' | 'percentage'>('amount');

  // Helper function to safely convert price to number
  const parsePrice = (price: any): number => {
    if (typeof price === 'number') return price;
    if (typeof price === 'string') return parseFloat(price) || 0;
    return 0;
  };

  // Helper function to get full image URL
  const getFullImageUrl = (imageUrl: string | undefined): string | null => {
    if (!imageUrl) return null;
    
    // If it's already a full URL, return as is
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      console.log('🔧 Already full URL:', imageUrl);
      return imageUrl;
    }
    
    // If it's just a filename, construct full URL
    const fullUrl = `${API_CONFIG.BASE_URL}/public/storage/${imageUrl}`;
    console.log('🔧 Constructed full URL from filename:', fullUrl);
    return fullUrl;
  };

  // Load data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Filter products when category or search changes
  useEffect(() => {
    filterProducts();
  }, [products, selectedCategory, searchQuery]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch categories and products in parallel
      const [categoriesData, productsData] = await Promise.all([
        getAllCategories(),
        getPublicProducts()
      ]);
      
      setCategories(categoriesData.filter(cat => cat.status === 'active'));
      const activeProducts = productsData.filter(prod => prod.status === 'active');
      setProducts(activeProducts);
      
      // Debug: Log first few products to see image data
      console.log('🔍 Products loaded:', activeProducts.slice(0, 3).map(p => ({
        id: p.id,
        name: p.name,
        main_image_url: p.main_image_url,
        image_url: p.image_url,
        image: p.image,
        hasMainImage: !!p.main_image_url,
        hasImageUrl: !!p.image_url,
        hasImage: !!p.image,
        fullMainImageUrl: getFullImageUrl(p.main_image_url),
        fullImageUrl: getFullImageUrl(p.image_url)
      })));
      
      // Check if any products have images
      const productsWithImages = activeProducts.filter(p => p.main_image_url || p.image_url || p.image);
      console.log(`📊 Total products: ${activeProducts.length}, Products with images: ${productsWithImages.length}`);
      
      if (productsWithImages.length > 0) {
        console.log('🖼️ Sample product with image:', productsWithImages[0]);
        console.log('🔗 Full main image URL:', getFullImageUrl(productsWithImages[0].main_image_url));
        console.log('🔗 Full image URL:', getFullImageUrl(productsWithImages[0].image_url));
        console.log('🌐 API Base URL:', API_CONFIG.BASE_URL);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load products and categories');
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    let filtered = products;

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter(product => product.category_id === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(query) ||
        product.description.toLowerCase().includes(query) ||
        product.code.toLowerCase().includes(query)
      );
    }

    setFilteredProducts(filtered);
  };

  const handleCategorySelect = async (categoryId: number) => {
    setSelectedCategory(categoryId);
    setSearchQuery(''); // Clear search when selecting category
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setSelectedCategory(null); // Clear category selection when searching
  };

  // Cart management functions
  const addToCart = (product: Product) => {
    const existingItem = cartItems.find(item => item.productId === product.id);
    
    if (existingItem) {
      updateCartItemQuantity(existingItem.id, existingItem.quantity + 1);
    } else {
      const newItem: CartItem = {
        id: Date.now(), // Simple ID generation
        productId: product.id,
        name: product.name,
        price: parsePrice(product.price),
        quantity: 1,
        image_url: getFullImageUrl(product.main_image_url) || getFullImageUrl(product.image_url) || getFullImageUrl(product.image) || undefined
      };
      setCartItems([...cartItems, newItem]);
    }
  };

  const updateCartItemQuantity = (itemId: number, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    
    setCartItems(cartItems.map(item => 
      item.id === itemId ? { ...item, quantity: newQuantity } : item
    ));
  };

  const removeFromCart = (itemId: number) => {
    setCartItems(cartItems.filter(item => item.id !== itemId));
  };

  const clearCart = () => {
    setCartItems([]);
  };

  // Calculate totals
  const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const discount = discountType === 'percentage' 
    ? (subtotal * discountValue) / 100 
    : discountValue;
  const vat = (subtotal - discount) * 0.05; // 5% VAT
  const total = subtotal - discount + vat;

  return (
    <div className="flex h-screen flex-col bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
                     {/* Logo */}
           <div className="flex items-center">
             <div className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center">
               <span className="text-white font-bold text-xl">POS</span>
             </div>
           </div>
          
          {/* Search Bar */}
          <div className="max-w-md w-full mx-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-slate-800 focus:border-slate-600 sm:text-sm"
                placeholder="Search products..."
              />
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center space-x-4">
            <button 
              className="bg-slate-700 text-white px-4 py-2 rounded-md flex items-center space-x-2 hover:bg-slate-800 transition-colors"
              onClick={() => navigate('/sales')}
            >
              <Radio className="h-4 w-4" />
              <span>Back to Sales</span>
            </button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel - Categories */}
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Categories</h2>
          </div>
          
          <div className="flex-1 overflow-y-auto p-2">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-32 text-red-500">
                <p className="text-sm">{error}</p>
              </div>
            ) : (
            <div className="space-y-1">
                {/* All Products Button */}
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                    selectedCategory === null
                      ? 'bg-slate-700 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  All Products
                </button>
                
                {/* Category Buttons */}
              {categories.map((category) => (
                <button
                  key={category.id}
                    onClick={() => handleCategorySelect(category.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                      selectedCategory === category.id
                      ? 'bg-slate-700 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
            )}
          </div>
        </div>
        
        {/* Center Panel - Menu Items */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Fast Moving Items Section */}
          <div className="p-4 border-b border-gray-200 bg-white">
            <h3 className="text-lg font-medium text-gray-800 flex items-center">
              <Flame className="h-5 w-5 text-orange-500 mr-2" />
              Fast Moving Items
            </h3>
            
            <div className="mt-3 flex space-x-4 overflow-x-auto pb-2">
              {/* Sample fast moving items - would be dynamic in real implementation */}
              {[1, 2, 3, 4, 5].map((item) => (
                <div 
                  key={item} 
                  className="flex-shrink-0 w-32 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {/* Add fast moving item */}}
                >
                  <div className="h-24 bg-gray-200 rounded-t-lg"></div>
                  <div className="p-2">
                    <div className="font-medium text-sm truncate">Popular Item {item}</div>
                    <div className="text-slate-700 font-bold text-sm">{formatCurrency(9.99)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Menu Items Grid */}
          <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-64 text-red-500">
                <p className="text-lg">{error}</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-gray-500">
                <div className="text-center">
                  <p className="text-lg font-medium">No products found</p>
                  <p className="text-sm mt-1">
                    {searchQuery ? 'Try adjusting your search terms' : 'No products available in this category'}
                  </p>
                </div>
              </div>
            ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {filteredProducts.map((product) => (
                <div 
                  key={product.id} 
                  className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => addToCart(product)}
                  >
                    <div className="h-36 bg-gray-200 relative overflow-hidden">
                      {(() => {
                        // Try original URL first, then constructed URL
                        const originalMainImageUrl = product.main_image_url;
                        const constructedMainImageUrl = getFullImageUrl(product.main_image_url);
                        
                        if (originalMainImageUrl) {
                          return (
                            <div className="relative w-full h-full">
                              <img
                                src={originalMainImageUrl}
                                alt={product.name}
                                className="w-full h-full object-cover transition-transform hover:scale-105"
                                onLoad={() => {
                                  console.log('✅ Original main image loaded successfully:', product.name, originalMainImageUrl);
                                }}
                                onError={(e) => {
                                  console.log('❌ Original main image failed to load:', product.name, originalMainImageUrl);
                                  console.log('🔄 Trying constructed URL:', constructedMainImageUrl);
                                  
                                  // Try the constructed URL
                                  const target = e.target as HTMLImageElement;
                                  if (constructedMainImageUrl && constructedMainImageUrl !== originalMainImageUrl) {
                                    target.src = constructedMainImageUrl;
                                    target.onload = () => {
                                      console.log('✅ Constructed main image loaded successfully:', product.name, constructedMainImageUrl);
                                    };
                                    target.onerror = () => {
                                      console.log('❌ Constructed main image also failed:', product.name, constructedMainImageUrl);
                                      target.style.display = 'none';
                                      target.nextElementSibling?.classList.remove('hidden');
                                    };
                                  } else {
                                    target.style.display = 'none';
                                    target.nextElementSibling?.classList.remove('hidden');
                                  }
                                }}
                              />
                              <div className="absolute inset-0 bg-gradient-to-br from-slate-500 to-slate-900 flex items-center justify-center hidden">
                                <div className="text-center text-white">
                                  <div className="text-2xl mb-1">📦</div>
                                  <span className="text-sm">No Image</span>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        
                        // Fallback to other image fields
                        const fallbackImageUrl = getFullImageUrl(product.image_url) || getFullImageUrl(product.image);
                        if (fallbackImageUrl) {
                          return (
                            <div className="relative w-full h-full">
                              <img
                                src={fallbackImageUrl}
                        alt={product.name} 
                                className="w-full h-full object-cover transition-transform hover:scale-105"
                                onLoad={() => {
                                  console.log('✅ Fallback image loaded successfully:', product.name, fallbackImageUrl);
                                }}
                                onError={(e) => {
                                  console.log('❌ Fallback image failed to load:', product.name, fallbackImageUrl);
                                  console.log('❌ Fallback error details:', e);
                                  // Fallback to placeholder if image fails to load
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                  target.nextElementSibling?.classList.remove('hidden');
                                }}
                              />
                              <div className="absolute inset-0 bg-gradient-to-br from-slate-500 to-slate-900 flex items-center justify-center hidden">
                                <div className="text-center text-white">
                                  <div className="text-2xl mb-1">📦</div>
                                  <span className="text-sm">No Image</span>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        
                        // No image available
                        return (
                          <div className="w-full h-full bg-gradient-to-br from-slate-500 to-slate-900 flex items-center justify-center">
                            <div className="text-center text-white">
                              <div className="text-2xl mb-1">📦</div>
                              <span className="text-sm">No Image</span>
                            </div>
                          </div>
                        );
                      })()}
                  </div>
                  <div className="p-3">
                    <h3 className="font-medium text-gray-900 truncate">{product.name}</h3>
                    <p className="text-sm text-gray-500 truncate">{product.description}</p>
                      <div className="mt-2 font-bold text-slate-700">{formatCurrency(parsePrice(product.price))}</div>
                      {product.category && (
                        <div className="text-xs text-gray-400 mt-1">{product.category.name}</div>
                      )}
                  </div>
                </div>
              ))}
            </div>
            )}
          </div>
        </div>
        
        {/* Right Panel - Cart */}
        <div className="w-80 bg-white border-l border-gray-200 flex flex-col overflow-hidden">
          {/* Order Type Selection */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex rounded-lg bg-gray-100 p-1">
              <button 
                onClick={() => setOrderType('pickup')}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                  orderType === 'pickup'
                    ? 'bg-white shadow-sm text-gray-900'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Pick Up
              </button>
              <button 
                onClick={() => setOrderType('delivery')}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                  orderType === 'delivery'
                    ? 'bg-white shadow-sm text-gray-900'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Delivery
              </button>
            </div>
          </div>
          
          {/* Customer Type Selection */}
          <div className="px-4 py-2 border-b border-gray-200">
            <div className="flex rounded-lg bg-gray-100 p-1">
              <button 
                onClick={() => setCustomerType('walking')}
                className={`flex-1 py-1 text-sm font-medium rounded-md transition-colors ${
                  customerType === 'walking'
                    ? 'bg-white shadow-sm text-gray-900'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Walking
              </button>
              <button 
                onClick={() => setCustomerType('customer')}
                className={`flex-1 py-1 text-sm font-medium rounded-md transition-colors ${
                  customerType === 'customer'
                    ? 'bg-white shadow-sm text-gray-900'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Customer
              </button>
            </div>
          </div>
          
          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-4">
            {cartItems.length > 0 ? (
              <div className="space-y-4">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex items-start space-x-3 border-b border-gray-100 pb-3">
                    {/* Product Image Thumbnail */}
                    <div className="w-12 h-12 bg-gray-200 rounded-md overflow-hidden flex-shrink-0">
                      {item.image_url ? (
                        <img 
                          src={item.image_url} 
                          alt={item.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : null}
                      <div className="w-full h-full flex items-center justify-center bg-gray-200">
                        <span className="text-gray-400 text-xs">📦</span>
                      </div>
                    </div>
                    
                    {/* Product Details */}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{item.name}</div>
                      <div className="text-sm text-gray-500">
                        {formatCurrency(item.price)} x {item.quantity}
                      </div>
                      {item.note && (
                        <div className="text-xs text-gray-400 italic mt-1">
                          Note: {item.note}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-col items-end space-y-2">
                      <div className="font-semibold">{formatCurrency(item.price * item.quantity)}</div>
                      
                      <div className="flex items-center space-x-2">
                        <button 
                          className="p-1 text-gray-400 hover:text-gray-600"
                          onClick={() => {/* TODO: Add note functionality */}}
                          title="Add note"
                        >
                          <PenLine className="h-4 w-4" />
                        </button>
                        <button 
                          className="p-1 text-red-400 hover:text-red-600"
                          onClick={() => removeFromCart(item.id)}
                          title="Remove item"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <div className="flex items-center border border-gray-200 rounded-md">
                          <button 
                            className="px-2 py-1 text-gray-500 hover:bg-gray-100"
                            onClick={() => updateCartItemQuantity(item.id, item.quantity - 1)}
                            title="Decrease quantity"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="px-2 text-sm font-medium">{item.quantity}</span>
                          <button 
                            className="px-2 py-1 text-gray-500 hover:bg-gray-100"
                            onClick={() => updateCartItemQuantity(item.id, item.quantity + 1)}
                            title="Increase quantity"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <div className="text-lg font-medium">Cart is empty</div>
                <p className="mt-1 text-sm">Add items from the menu</p>
              </div>
            )}
          </div>
          
                     {/* Payment Section */}
           <div className="border-t border-gray-200 p-4 bg-gray-50">
            
            {/* Discount */}
            <div className="mb-4 flex items-center space-x-2">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Discount</label>
                <div className="flex">
                  <DecimalInput
                    value={discountValue}
                    onCommit={setDiscountValue}
                    min={0}
                    max={discountType === 'percentage' ? 100 : undefined}
                    emptyWhenZero
                    className="block w-full rounded-l-md border-gray-300 shadow-sm focus:border-slate-600 focus:ring-slate-800 sm:text-sm"
                    placeholder="0"
                  />
                <button 
                    onClick={() => setDiscountType('amount')}
                    className={`px-3 py-2 text-sm font-medium rounded-r-md border border-l-0 transition-colors ${
                      discountType === 'amount'
                        ? 'bg-slate-700 text-white border-blue-500'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  LKR
                </button>
                <button 
                    onClick={() => setDiscountType('percentage')}
                    className={`px-3 py-2 text-sm font-medium rounded-md border border-l-0 transition-colors ${
                      discountType === 'percentage'
                        ? 'bg-slate-700 text-white border-blue-500'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  %
                </button>
                </div>
              </div>
            </div>
            
            {/* Totals */}
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Discount:</span>
                <span className="font-medium text-red-500">-{formatCurrency(discount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">VAT (5%):</span>
                <span className="font-medium">{formatCurrency(vat)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
                <span>Total:</span>
                <span>{formatCurrency(total)}</span>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex space-x-2">
              <button 
                onClick={clearCart}
                disabled={cartItems.length === 0}
                className="px-4 py-2 border border-red-300 text-red-600 rounded-md hover:bg-red-50 transition-colors text-sm font-medium flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear Cart
              </button>
              <button 
                onClick={() => {/* TODO: Implement order placement */}}
                disabled={cartItems.length === 0}
                className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-md hover:bg-slate-800 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Place Order
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Static UI - No modals for now */}
    </div>
  );
};

export default POS;
